import { fetchWithChatFeedback } from './chatFeedbackRequired';
/**
 * Services COMBO (Combined Synopsis/Solicitation) API Functions
 *
 * Typed client mirroring `foprFunctions.ts`. Handles document upload,
 * extraction, form submission, data retrieval, and fillable-PDF export for the
 * Services COMBO workflow (stages 700 Upload / 701 Form).
 *
 * Backend contract (all require the session cookie / X-User-Email header):
 *   POST   /api/services-combo/upload-document          (multipart)
 *   GET    /api/services-combo/{flow_id}/documents
 *   DELETE /api/services-combo/{flow_id}/documents/{doc_type}
 *   POST   /api/services-combo/run                       ({flow_id, force_refresh})
 *   PUT    /api/services-combo/{flow_id}/form
 *   GET    /api/services-combo/{flow_id}/data
 *   POST   /api/services-combo/generate-pdf              ({flow_id}) -> application/pdf
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// ── Types (mirror the pydantic models) ──────────────────────────────────────

/** One row of the Section V (Provisions) / Section VI (Clauses) tables. */
export interface ClauseEntry {
  number: string;
  title: string;
  kind: 'provision' | 'clause';
  regulation: 'FAR' | 'DFARS';
  incorporated: boolean;
  effective_date?: string;
  alternate?: string | null;
  full_text?: string | null;
}

/** Contract Line Item Number entry (Services-specific: ServicesCLIN). */
export interface CLIN {
  number: string;
  prefix?: string;
  description: string;
  quantity?: number;
  unit?: string;
  unit_price?: string; // editable currency; Total Amount = unit_price × quantity
  total_cost?: string; // computed Total Amount
}

/** Metadata for a document in the attachment list (reused from FOPR). */
export interface AttachmentMeta {
  number: number;
  name: string;
  doc_type?: string | null;
  date?: string | null;
  page_count?: number | null;
}

/** SCA Service Contract Act wage determination metadata. */
export interface ServicesWageDetermination {
  number: string;
  date: string;
  revision?: string | null;
  county_state?: string | null;
  classification?: string | null;
}

/** Complete Services COMBO data model (mirrors ServicesComboData pydantic). */
export interface ServicesComboData {
  // --- auto-filled: doc extraction ---
  requirement_title: string;
  requirement_description: string;
  inline_requirement_summary?: string; // short noun phrase for the Section II "...is soliciting for a ___" sentence
  inline_pws_sow_type: string; // "PWS" | "SOW" — Section III inline document-type choice

  period_of_performance: string; // canonical PDF display string; derived from the two dates below when set
  pop_start_date: string; // ISO YYYY-MM-DD; CO-set via the form date picker
  pop_end_date: string; // ISO YYYY-MM-DD; CO-set via the form date picker
  place_of_performance: string;
  naics_code: string;
  naics_size_standard: string;
  psc_code: string;
  wage_determination: ServicesWageDetermination | null;
  technical_subfactors?: { name: string; instructions: string; measure_of_merit: string }[];
  clins: CLIN[];

  // --- auto-filled: CLS XML ---
  provisions: ClauseEntry[];
  clauses: ClauseEntry[];

  // --- auto-filled: RFO/lookups ---
  fac_date: string;
  clause_dates: Record<string, string>;
  dfars_notice_date: string;
  afac_date: string;

  // --- manual ---
  solicitation_number: string;
  solicitation_issue_date: string;
  response_deadline: string;
  questions_due: string;
  poc: string;
  poc_email: string;
  poc_phone: string;
  alternate_poc: string;
  contracting_officer: string;
  submission_emails: string[];
  site_visit: boolean;
  site_visit_datetime: string;
  site_visit_location: string;
  competition_type: string; // "competitive" | "sole_source"
  set_aside: string;
  evaluation_approach: string; // "LPTA" | "trade-off"
  evaluation_factors: string[];
  has_options: boolean;
  funds_available: boolean;

  // --- attachments + badges ---
  attachments: AttachmentMeta[];
  meta_auto_filled_fields?: string[];
  meta_user_edited_fields?: string[];
}

export interface ServicesComboDocumentInfo {
  filename: string;
  content_type: string;
  size: number;
}

export interface ServicesComboUploadResponse {
  success: boolean;
  doc_type: string;
  filename: string;
  file_size: number;
  content_type: string;
}

/** Shape of the documents-list response: keyed by doc_type + a required_complete flag. */
export type ServicesComboDocuments = Record<string, ServicesComboDocumentInfo> & {
  required_complete?: boolean;
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function getUserEmail(): string {
  return localStorage.getItem('userEmail') || (window as any).userEmail || '';
}

/**
 * Extract a user-facing message from a non-ok response. Mirrors FOPR's error
 * handling: prefers `detail`, but also surfaces the repo's error envelope fields
 * (`error_code` / `technical_detail`) on the thrown Error for analytics consumers.
 */
async function throwFromResponse(response: Response, fallback: string): Promise<never> {
  const body = await response.json().catch(() => ({ detail: fallback }));
  const detailValue = (body && (body.detail ?? body.error)) as unknown;
  let message: string;
  if (typeof detailValue === 'string') {
    message = detailValue;
  } else if (detailValue && typeof detailValue === 'object') {
    const d = detailValue as Record<string, unknown>;
    message =
      (typeof d.error === 'string' && d.error) ||
      (typeof d.message === 'string' && d.message) ||
      JSON.stringify(detailValue);
  } else {
    message = `${fallback} (status ${response.status})`;
  }

  const err = new Error(message) as Error & {
    error_code?: string;
    technical_detail?: string;
  };
  const envelope = (detailValue && typeof detailValue === 'object'
    ? (detailValue as Record<string, unknown>)
    : body) as Record<string, unknown>;
  if (typeof envelope?.error_code === 'string') err.error_code = envelope.error_code;
  if (typeof envelope?.technical_detail === 'string') {
    err.technical_detail = envelope.technical_detail;
  }
  throw err;
}

function jsonHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const userEmail = getUserEmail();
  if (userEmail) headers['X-User-Email'] = userEmail;
  return headers;
}

// ── Endpoints ────────────────────────────────────────────────────────────────

/**
 * Upload a single Services COMBO document (multipart).
 * @param flowId - The flow/session identifier
 * @param docType - One of the valid Services COMBO doc types (e.g. "sow", "cls_xml")
 * @param file - The file to upload
 */
export async function uploadServicesComboDocument(
  flowId: string,
  docType: string,
  file: File
): Promise<ServicesComboUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('flow_id', flowId);
  formData.append('doc_type', docType);

  const headers: Record<string, string> = {};
  const userEmail = getUserEmail();
  if (userEmail) headers['X-User-Email'] = userEmail;

  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/services-combo/upload-document`, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) await throwFromResponse(response, 'Upload failed');
  return response.json();
}

/**
 * List uploaded Services COMBO documents for a flow.
 * @returns Object keyed by doc_type plus a `required_complete` boolean.
 */
export async function getServicesComboDocuments(
  flowId: string
): Promise<ServicesComboDocuments> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/services-combo/${flowId}/documents`, {
    method: 'GET',
    headers: jsonHeaders(),
    credentials: 'include',
  });

  if (!response.ok) await throwFromResponse(response, 'Failed to get documents');
  return response.json();
}

/**
 * Delete a specific Services COMBO document.
 */
export async function deleteServicesComboDocument(
  flowId: string,
  docType: string
): Promise<{ status: string }> {
  const response = await fetchWithChatFeedback(
    `${API_BASE_URL}/api/services-combo/${flowId}/documents/${docType}`,
    {
      method: 'DELETE',
      headers: jsonHeaders(),
      credentials: 'include',
    }
  );

  if (!response.ok) await throwFromResponse(response, 'Delete failed');
  return response.json();
}

/**
 * Run Services COMBO extraction against uploaded documents.
 * @param forceRefresh - When true, bypass the cached stage-701 result.
 * @returns Extracted ServicesComboData
 */
export async function runServicesComboExtraction(
  flowId: string,
  forceRefresh = false
): Promise<ServicesComboData> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/services-combo/run`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ flow_id: flowId, force_refresh: forceRefresh }),
    credentials: 'include',
  });

  if (!response.ok) await throwFromResponse(response, 'Failed to run extraction');
  return response.json();
}

/**
 * Submit the completed Services COMBO form (validates + merges server-side).
 * @param formData - Partial ServicesComboData; may include a `_meta` badge block.
 */
export async function submitServicesComboForm(
  flowId: string,
  formData: Partial<ServicesComboData> & { _meta?: object }
): Promise<{ status: string }> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/services-combo/${flowId}/form`, {
    method: 'PUT',
    headers: jsonHeaders(),
    body: JSON.stringify(formData),
    credentials: 'include',
  });

  if (!response.ok) await throwFromResponse(response, 'Failed to submit form');
  return response.json();
}

/**
 * Get the saved Services COMBO data for a flow (extraction + form result).
 * Throws on 404 if extraction has not been run.
 */
export async function getServicesComboData(flowId: string): Promise<ServicesComboData> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/services-combo/${flowId}/data`, {
    method: 'GET',
    headers: jsonHeaders(),
    credentials: 'include',
  });

  if (!response.ok) await throwFromResponse(response, 'Failed to get Services COMBO data');
  return response.json();
}

/**
 * Generate and download the filled, editable COMBO PDF for a flow.
 * Triggers a browser download of `Combined_Synopsis_{solicitation_number}.pdf`.
 */
export async function generateServicesComboPdf(flowId: string): Promise<void> {
  const response = await fetchWithChatFeedback(`${API_BASE_URL}/api/services-combo/generate-pdf`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ flow_id: flowId }),
    credentials: 'include',
  });

  if (!response.ok) await throwFromResponse(response, 'Failed to generate PDF');

  // Prefer the server-provided filename from Content-Disposition; fall back to flow id.
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `Combined_Synopsis_${flowId}.pdf`;

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
